// src/pages/dashboard/branch/settings/componenets/IngredientSettings.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FiPlus,
    FiEdit2,
    FiTrash2,
    FiSearch,
    FiPackage,
    FiLayers,
    FiChevronDown,
    FiChevronRight,
    FiUsers
} from 'react-icons/fi';
import Card from '../../../../../components/ui/Card';
import Button from '../../../../../components/ui/Button';
import InputField from '../../../../../components/ui/InputField';
import Badge from '../../../../../components/ui/Badge';
import ConfirmDeleteModal from '../../../../../components/ui/ConfirmDeleteModal';
import { Branch } from '../../../../../utils/supabaseClient';
import {
    Ingredient,
    Group,
    GroupWithIngredients,
    IngredientFormData,
    GroupFormData
} from '../../../../../types/ingredient';
import { ingredientService } from '../../../../../services/ingredientService';
import { motion } from 'framer-motion';

type IngredientSettingsProps = {
    restaurant: Branch | null;
    updateRestaurant: (updates: Partial<Branch>) => void;
    branchId: string | undefined;
};

const IngredientSettings: React.FC<IngredientSettingsProps> = ({
    restaurant,
    updateRestaurant,
    branchId
}) => {
    const { t } = useTranslation();

    // State management
    const [activeTab, setActiveTab] = useState<'ingredients' | 'groups'>('ingredients');
    const [loading, setLoading] = useState(true);

    // Ingredients state
    const [ingredients, setIngredients] = useState<Ingredient[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [displayedIngredients, setDisplayedIngredients] = useState<Ingredient[]>([]);
    const searchDebounceTimer = useRef<NodeJS.Timeout | null>(null);
    const [showIngredientModal, setShowIngredientModal] = useState(false);
    const [editingIngredient, setEditingIngredient] = useState<Ingredient | null>(null);
    const [ingredientFormData, setIngredientFormData] = useState<IngredientFormData>({
        name: '',
        arName: ''
    });
    const [isCreatingIngredient, setIsCreatingIngredient] = useState(false);
    const [deleteIngredientId, setDeleteIngredientId] = useState<string | null>(null);

    // Groups state
    const [groups, setGroups] = useState<GroupWithIngredients[]>([]);
    const [groupSearchTerm, setGroupSearchTerm] = useState('');
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState<GroupWithIngredients | null>(null);
    const [groupFormData, setGroupFormData] = useState<GroupFormData>({
        name: '',
        selectedIngredients: []
    });
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [availableIngredients, setAvailableIngredients] = useState<Ingredient[]>([]);
    const [ingredientModalSearch, setIngredientModalSearch] = useState('');

    // Load data
    useEffect(() => {
        if (branchId) {
            loadData();
        }
    }, [branchId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [ingredientsData, groupsData] = await Promise.all([
                ingredientService.getIngredientsByBranch(branchId!),
                ingredientService.getGroupsByBranch(branchId!)
            ]);
            setIngredients(ingredientsData);
            setDisplayedIngredients(ingredientsData);
            setGroups(groupsData);
        } catch (error) {
            console.error('Error loading ingredient data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Debounced search function
    const performSearch = useCallback(async (search: string) => {
        if (!branchId) return;

        if (!search.trim()) {
            setDisplayedIngredients(ingredients);
            setSearchLoading(false);
            return;
        }

        try {
            setSearchLoading(true);
            const results = await ingredientService.searchIngredients(branchId, search);
            setDisplayedIngredients(results);
        } catch (error) {
            console.error('Error searching ingredients:', error);
            setDisplayedIngredients([]);
        } finally {
            setSearchLoading(false);
        }
    }, [branchId, ingredients]);

    // Handle search term change with debouncing
    useEffect(() => {
        // Clear previous timer
        if (searchDebounceTimer.current) {
            clearTimeout(searchDebounceTimer.current);
        }

        // Set new timer for debounced search
        setSearchLoading(true);
        searchDebounceTimer.current = setTimeout(() => {
            performSearch(searchTerm);
        }, 300); // 300ms debounce delay

        return () => {
            if (searchDebounceTimer.current) {
                clearTimeout(searchDebounceTimer.current);
            }
        };
    }, [searchTerm, performSearch]);

    // Ingredient operations
    const handleCreateIngredient = async () => {
        if (!ingredientFormData.name.trim() || !branchId) return;

        setIsCreatingIngredient(true);
        try {
            const newIngredient = await ingredientService.createIngredient({
                name: ingredientFormData.name.trim(),
                branch_id: branchId,
                ar: ingredientFormData.arName || undefined
            });

            setIngredients(prev => [...prev, newIngredient]);
            setDisplayedIngredients(prev => [...prev, newIngredient]);
            setShowIngredientModal(false);
            resetIngredientForm();
        } catch (error) {
            console.error('Error creating ingredient:', error);
        } finally {
            setIsCreatingIngredient(false);
        }
    };

    const handleUpdateIngredient = async () => {
        if (!editingIngredient || !ingredientFormData.name.trim()) return;

        setIsCreatingIngredient(true);
        try {
            const updatedIngredient = await ingredientService.updateIngredient(editingIngredient.id, {
                name: ingredientFormData.name.trim(),
                ar: ingredientFormData.arName || undefined
            });

            setIngredients(prev => prev.map(ingredient =>
                ingredient.id === editingIngredient.id ? updatedIngredient : ingredient
            ));
            setDisplayedIngredients(prev => prev.map(ingredient =>
                ingredient.id === editingIngredient.id ? updatedIngredient : ingredient
            ));
            setShowIngredientModal(false);
            resetIngredientForm();
        } catch (error) {
            console.error('Error updating ingredient:', error);
        } finally {
            setIsCreatingIngredient(false);
        }
    };

    const handleDeleteIngredient = async (id: string) => {
        try {
            await ingredientService.deleteIngredient(id);
            setIngredients(prev => prev.filter(ingredient => ingredient.id !== id));
            setDisplayedIngredients(prev => prev.filter(ingredient => ingredient.id !== id));
            setDeleteIngredientId(null);
        } catch (error) {
            console.error('Error deleting ingredient:', error);
        }
    };

    const openIngredientModal = (ingredient?: Ingredient) => {
        if (ingredient) {
            setEditingIngredient(ingredient);
            setIngredientFormData({
                name: ingredient.name,
                arName: typeof ingredient.ar === 'string' ? ingredient.ar : ''
            });
        } else {
            setEditingIngredient(null);
            resetIngredientForm();
        }
        setShowIngredientModal(true);
    };

    const resetIngredientForm = () => {
        setIngredientFormData({ name: '', arName: '' });
        setEditingIngredient(null);
    };

    // Group operations
    const loadAvailableIngredients = async (groupId?: string) => {
        if (!branchId) return;
        try {
            const available = await ingredientService.getAvailableIngredientsForGroup(branchId, groupId);
            setAvailableIngredients(available);
        } catch (error) {
            console.error('Error loading available ingredients:', error);
        }
    };

    const handleCreateGroup = async () => {
        if (!groupFormData.name.trim() || !branchId) return;

        setIsCreatingGroup(true);
        try {
            const newGroup = await ingredientService.createGroup({
                name: groupFormData.name.trim(),
                branch_id: branchId
            });

            if (groupFormData.selectedIngredients.length > 0) {
                await ingredientService.addIngredientsToGroup({
                    group_id: newGroup.id,
                    ingredient_ids: groupFormData.selectedIngredients
                });
            }

            // Reload groups to get the updated data with ingredients
            await loadData();
            setShowGroupModal(false);
            resetGroupForm();
        } catch (error) {
            console.error('Error creating group:', error);
        } finally {
            setIsCreatingGroup(false);
        }
    };

    const handleUpdateGroup = async () => {
        if (!editingGroup || !groupFormData.name.trim()) return;

        setIsCreatingGroup(true);
        try {
            await ingredientService.updateGroup(editingGroup.id, {
                name: groupFormData.name.trim()
            });

            // Update ingredients in group
            const currentGroup = groups.find(g => g.id === editingGroup.id);
            const currentIngredientIds = currentGroup?.ingredients?.map(i => i.id) || [];

            // Remove ingredients that are no longer selected
            for (const ingredientId of currentIngredientIds) {
                if (!groupFormData.selectedIngredients.includes(ingredientId)) {
                    await ingredientService.removeIngredientFromGroup(editingGroup.id, ingredientId);
                }
            }

            // Add new ingredients
            const newIngredientIds = groupFormData.selectedIngredients.filter(
                id => !currentIngredientIds.includes(id)
            );

            if (newIngredientIds.length > 0) {
                await ingredientService.addIngredientsToGroup({
                    group_id: editingGroup.id,
                    ingredient_ids: newIngredientIds
                });
            }

            // Reload groups to get the updated data
            await loadData();
            setShowGroupModal(false);
            resetGroupForm();
        } catch (error) {
            console.error('Error updating group:', error);
        } finally {
            setIsCreatingGroup(false);
        }
    };

    const handleDeleteGroup = async (id: string) => {
        try {
            await ingredientService.deleteGroup(id);
            setGroups(prev => prev.filter(group => group.id !== id));
            setDeleteGroupId(null);
        } catch (error) {
            console.error('Error deleting group:', error);
        }
    };

    const openGroupModal = async (group?: GroupWithIngredients) => {
        await loadAvailableIngredients(group?.id);

        if (group) {
            setEditingGroup(group);
            setGroupFormData({
                name: group.name,
                selectedIngredients: group.ingredients?.map(i => i.id) || []
            });
        } else {
            setEditingGroup(null);
            resetGroupForm();
        }
        setShowGroupModal(true);
    };

    const resetGroupForm = () => {
        setGroupFormData({ name: '', selectedIngredients: [] });
        setEditingGroup(null);
        setIngredientModalSearch('');
    };

    const toggleGroupExpansion = (groupId: string) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    // Filter functions
    const filteredIngredients = displayedIngredients;

    const filteredGroups = groups.filter(group =>
        group.name.toLowerCase().includes(groupSearchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <Card>
                <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="space-y-3">
                        <div className="h-16 bg-gray-200 rounded"></div>
                        <div className="h-16 bg-gray-200 rounded"></div>
                        <div className="h-16 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <div className="space-y-6">

            <motion.div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {t('common.ingredientManagement.title')}
                </h2>
            </motion.div>
            <Card>
                {/* Tabs */}
                <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-6">
                    <button
                        onClick={() => setActiveTab('ingredients')}
                        className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'ingredients'
                            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                    >
                        <FiPackage className="h-4 w-4 mr-2" />
                        {t('settings.tabs.ingredients')}
                    </button>
                    <button
                        onClick={() => setActiveTab('groups')}
                        className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'groups'
                            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                    >
                        <FiLayers className="h-4 w-4 mr-2" />
                        {t('menuManagement.ingredientGroups')}
                    </button>
                </div>

                {/* Ingredients Tab */}
                {activeTab === 'ingredients' && (
                    <div className="space-y-4">
                        {/* Search and Add */}
                        <div className="flex items-center justify-between">
                            <div className="flex-1 max-w-md">
                                <InputField
                                    placeholder={t('menuManagement.searchIngredientsPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <Button
                                onClick={() => openIngredientModal()}
                                leftIcon={<FiPlus />}
                            >
                                {t('menuManagement.addIngredient')}
                            </Button>
                        </div>

                        {/* Ingredients List */}
                        <div className="grid gap-3">
                            {filteredIngredients.length === 0 ? (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    <FiPackage className="mx-auto h-12 w-12 mb-4 opacity-50" />
                                    <h3 className="text-lg font-medium mb-2">{t('common.noIngredientsFound', 'No ingredients found')}</h3>
                                    <p className="text-sm">
                                        {searchTerm ? t('common.tryAdjustingSearch', 'Try adjusting your search terms') : t('common.startByAddingFirst', 'Start by adding your first ingredient')}
                                    </p>
                                </div>
                            ) : (
                                filteredIngredients.map((ingredient) => (
                                    <div key={ingredient.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div>
                                            <h4 className="font-medium text-gray-900 dark:text-white">
                                                {ingredient.name}
                                            </h4>
                                            {ingredient.ar && typeof ingredient.ar === 'string' && (
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {ingredient.ar}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => openIngredientModal(ingredient)}
                                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                title={t('common.edit')}
                                            >
                                                <FiEdit2 className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => setDeleteIngredientId(ingredient.id)}
                                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                title={t('common.delete')}
                                            >
                                                <FiTrash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Groups Tab */}
                {activeTab === 'groups' && (
                    <div className="space-y-4">
                        {/* Search and Add */}
                        <div className="flex items-center justify-between">
                            <div className="flex-1 max-w-md">
                                <InputField
                                    placeholder={t('common.searchGroups', 'Search groups...')}
                                    value={groupSearchTerm}
                                    onChange={(e) => setGroupSearchTerm(e.target.value)}
                                />
                            </div>
                            <Button
                                onClick={() => openGroupModal()}
                                leftIcon={<FiPlus />}
                            >
                                {t('menuManagement.addIngredientGroup')}
                            </Button>
                        </div>

                        {/* Groups List */}
                        <div className="grid gap-3">
                            {filteredGroups.length === 0 ? (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    <FiLayers className="mx-auto h-12 w-12 mb-4 opacity-50" />
                                    <h3 className="text-lg font-medium mb-2">{t('common.noGroupsFound', 'No groups found')}</h3>
                                    <p className="text-sm">
                                        {groupSearchTerm ? t('common.tryAdjustingSearch', 'Try adjusting your search terms') : t('common.startByCreatingFirst', 'Start by creating your first ingredient group')}
                                    </p>
                                </div>
                            ) : (
                                filteredGroups.map((group) => {
                                    const isExpanded = expandedGroups.has(group.id);
                                    return (
                                        <div key={group.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700">
                                                <div className="flex items-center space-x-3">
                                                    <button
                                                        onClick={() => toggleGroupExpansion(group.id)}
                                                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                                    >
                                                        {isExpanded ? (
                                                            <FiChevronDown className="h-4 w-4" />
                                                        ) : (
                                                            <FiChevronRight className="h-4 w-4" />
                                                        )}
                                                    </button>
                                                    <div>
                                                        <h4 className="font-medium text-gray-900 dark:text-white">
                                                            {group.name}
                                                        </h4>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                                            {group.ingredient_count || 0} {t('common.ingredients', 'ingredients')}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <Badge variant="secondary">
                                                        {group.ingredient_count || 0}
                                                    </Badge>
                                                    <button
                                                        onClick={() => openGroupModal(group)}
                                                        className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                        title={t('common.edit')}
                                                    >
                                                        <FiEdit2 className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteGroupId(group.id)}
                                                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title={t('common.delete')}
                                                    >
                                                        <FiTrash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Group Ingredients */}
                                            {isExpanded && group.ingredients && group.ingredients.length > 0 && (
                                                <div className="p-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                        {group.ingredients.map((ingredient) => (
                                                            <div key={ingredient.id} className="flex items-center space-x-2 text-sm">
                                                                <FiPackage className="h-3 w-3 text-gray-400" />
                                                                <span className="text-gray-600 dark:text-gray-300" title={ingredient.ar || ingredient.name}>
                                                                    {ingredient.name}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </Card>

            {/* Ingredient Modal */}
            {showIngredientModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                {editingIngredient ? t('menuManagement.editIngredient') : t('menuManagement.addIngredient')}
                            </h3>
                            <button
                                onClick={() => setShowIngredientModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ×
                            </button>
                        </div>

                        <div className="space-y-4">
                            <InputField
                                label={t('common.ingredientName', 'Ingredient Name')}
                                placeholder={t('common.enterIngredientName', 'Enter ingredient name')}
                                value={ingredientFormData.name}
                                onChange={(e) => setIngredientFormData(prev => ({ ...prev, name: e.target.value }))}
                                required
                            />

                            <InputField
                                label={t('common.arabicName', 'Arabic Name (Optional)')}
                                placeholder={t('common.enterArabicName', 'أدخل اسم المكون بالعربية')}
                                value={ingredientFormData.arName}
                                onChange={(e) => setIngredientFormData(prev => ({ ...prev, arName: e.target.value }))}
                                dir="rtl"
                            />
                        </div>

                        <div className="flex items-center justify-end space-x-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => setShowIngredientModal(false)}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                onClick={editingIngredient ? handleUpdateIngredient : handleCreateIngredient}
                                isLoading={isCreatingIngredient}
                                disabled={!ingredientFormData.name.trim()}
                            >
                                {editingIngredient ? t('common.update') : t('common.create')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Group Modal */}
            {showGroupModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                {editingGroup ? t('menuManagement.editIngredientGroup') : t('menuManagement.addIngredientGroup')}
                            </h3>
                            <button
                                onClick={() => setShowGroupModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                                title={t('common.close')}
                            >
                                ×
                            </button>
                        </div>

                        <div className="space-y-6">
                            <InputField
                                label={t('common.groupName', 'Group Name')}
                                placeholder={t('common.enterGroupName', "Enter group name (e.g., 'Vegetables', 'Proteins')")}
                                value={groupFormData.name}
                                onChange={(e) => setGroupFormData(prev => ({ ...prev, name: e.target.value }))}
                                required
                            />

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        {t('common.selectIngredients', 'Select Ingredients')}
                                    </label>
                                    {groupFormData.selectedIngredients.length > 0 && (
                                        <Badge variant="primary" size="sm">
                                            {groupFormData.selectedIngredients.length} {t('common.selected', 'selected')}
                                        </Badge>
                                    )}
                                </div>

                                {/* Search Input */}
                                <div className="relative mb-3">
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder={t('common.searchIngredients', 'Search ingredients...')}
                                        value={ingredientModalSearch}
                                        onChange={(e) => setIngredientModalSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg 
                                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                                   focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                </div>

                                {/* Select All / Deselect All Buttons */}
                                {availableIngredients.length > 0 && (
                                    <div className="flex items-center gap-2 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const allIds = [...availableIngredients, ...(editingGroup?.ingredients || [])].map(i => i.id);
                                                setGroupFormData(prev => ({
                                                    ...prev,
                                                    selectedIngredients: [...new Set(allIds)]
                                                }));
                                            }}
                                            className="text-xs text-primary hover:text-primary/80 font-medium"
                                        >
                                            {t('common.selectAll', 'Select All')}
                                        </button>
                                        <span className="text-gray-300 dark:text-gray-600">|</span>
                                        <button
                                            type="button"
                                            onClick={() => setGroupFormData(prev => ({
                                                ...prev,
                                                selectedIngredients: []
                                            }))}
                                            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-medium"
                                        >
                                            {t('common.deselectAll', 'Deselect All')}
                                        </button>
                                    </div>
                                )}

                                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 max-h-72 overflow-y-auto">
                                    {availableIngredients.length === 0 ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                            {t('common.noIngredientsAvailable', 'No ingredients available. Create some ingredients first.')}
                                        </p>
                                    ) : (
                                        (() => {
                                            const allIngredients = [...availableIngredients, ...(editingGroup?.ingredients || [])];
                                            const filteredIngredients = ingredientModalSearch.trim()
                                                ? allIngredients.filter(ingredient =>
                                                    ingredient.name.toLowerCase().includes(ingredientModalSearch.toLowerCase())
                                                )
                                                : allIngredients;

                                            if (filteredIngredients.length === 0) {
                                                return (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                                        {t('common.noMatchingIngredients', 'No matching ingredients found')}
                                                    </p>
                                                );
                                            }

                                            return (
                                                <div className="grid grid-cols-2 gap-2">
                                                    {filteredIngredients.map((ingredient) => {
                                                        const isSelected = groupFormData.selectedIngredients.includes(ingredient.id);
                                                        return (
                                                            <label
                                                                key={ingredient.id}
                                                                className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors
                                                                    ${isSelected
                                                                        ? 'bg-primary/10 border border-primary/30'
                                                                        : 'bg-gray-50 dark:bg-gray-700/50 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-700'
                                                                    }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setGroupFormData(prev => ({
                                                                                ...prev,
                                                                                selectedIngredients: [...prev.selectedIngredients, ingredient.id]
                                                                            }));
                                                                        } else {
                                                                            setGroupFormData(prev => ({
                                                                                ...prev,
                                                                                selectedIngredients: prev.selectedIngredients.filter(id => id !== ingredient.id)
                                                                            }));
                                                                        }
                                                                    }}
                                                                    className="rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                                                                />
                                                                <span className={`text-sm truncate ${isSelected ? 'text-primary font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                                                                    {ingredient.name}
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => setShowGroupModal(false)}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                onClick={editingGroup ? handleUpdateGroup : handleCreateGroup}
                                isLoading={isCreatingGroup}
                                disabled={!groupFormData.name.trim()}
                            >
                                {editingGroup ? t('common.update') : t('common.create')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Ingredient Modal */}
            {deleteIngredientId && (
                <ConfirmDeleteModal
                    isOpen={!!deleteIngredientId}
                    onCancel={() => setDeleteIngredientId(null)}
                    onConfirm={() => handleDeleteIngredient(deleteIngredientId)}
                    title={t('menuManagement.deleteIngredient')}
                    message={t('common.deleteIngredientConfirm', 'Are you sure you want to delete this ingredient? This action cannot be undone.')}
                />
            )}

            {/* Delete Group Modal */}
            {deleteGroupId && (
                <ConfirmDeleteModal
                    isOpen={!!deleteGroupId}
                    onCancel={() => setDeleteGroupId(null)}
                    onConfirm={() => handleDeleteGroup(deleteGroupId)}
                    title={t('menuManagement.deleteIngredientGroup')}
                    message={t('common.deleteGroupConfirm', 'Are you sure you want to delete this group? This will also remove all ingredient relationships in this group.')}
                />
            )}
        </div>
    );
};

export default IngredientSettings;